// المتحكّم: بوّابة الدخول، التنقّل، توزيع الأحداث، ودورة المزامنة.

import { api, ApiError, isOnline, onConnectivity } from './api.js';
import {
  state, loadFor, clearLocal, lastUser, saveNow, applyTheme,
  removeEntries, restoreEntries, pendingCount
} from './store.js';
import * as store from './store.js';
import { sync, startAutoSync, onSyncChange, SyncStatus, loadRange } from './sync.js';
import { baseCur as baseCurrency } from './store.js';
import { calcModal as calcModalHtml, updateCalcModal,
         helpSheet, setHelpTitles } from './ui.js';
import * as help from './help.js';

setHelpTitles(help.SHORT);   // عناوين شريط التنقّل أسفل ورقة الدليل
import * as calc from './calc.js';

import * as authView     from './views/auth.js';
import * as entryView    from './views/entry.js';
import * as dayView      from './views/day.js';
import * as splitView    from './views/split.js';
import * as textView     from './views/text.js';
import * as classifyView from './views/classify.js';
import * as logView      from './views/log.js';
import * as editView     from './views/edit.js';
import * as reportView   from './views/report.js';
import * as settingsView from './views/settings.js';
import * as purgeView    from './views/purge.js';
import * as accountView  from './views/account.js';
import * as devicesView  from './views/devices.js';

const root     = document.getElementById('app');
const nav      = document.getElementById('nav');
const toastEl  = document.getElementById('toast');
const toastMsg = document.getElementById('toastMsg');
const toastAct = document.getElementById('toastAct');
const banner   = document.getElementById('banner');
const modal    = document.getElementById('modal');

const MAIN = { entry: entryView, log: logView, report: reportView, settings: settingsView };
const SUBS = { day: dayView, split: splitView, text: textView, classify: classifyView,
               edit: editView, purge: purgeView, account: accountView,
               devices: devicesView };

const app = {
  ready: false,
  session: null,        // { user, household } — موجود يعني مصادَقاً
  screen: 'entry',
  sub: null,

  authDraft:  authView.freshDraft(),
  draft:      null,
  dayDraft:   null,
  splitDraft: null,
  textDraft:  null,
  clsDraft:   null,
  editDraft:  null,
  purgeDraft: null,
  accountDraft: null,
  devicesDraft: null,
  calcModal:  null,
  helpKey:    null,        // قسم الدليل المفتوح — null يعني مغلق
  loading:    false,       // جلب فترة جارٍ
  dataErr:    null,        // 'offline' أو رسالة — تعرضها الشاشة بدل بيانات ناقصة
  rep:        reportView.freshState(),

  /* ------------------------------ العرض ------------------------------ */

  currentView(){
    // رمز الاسترجاع يُعرض بعد نجاح التسجيل — أي والجلسة قائمة فعلاً.
    // بلا هذا الشرط تقفز الواجهة مباشرة إلى شاشة التسجيل فيضيع الرمز إلى الأبد
    // قبل أن يراه المستخدم، وهو وسيلته الوحيدة لاستعادة حسابه.
    if (!this.session || this.authDraft?.showRecovery) return authView;
    if (this.sub && SUBS[this.sub]) return SUBS[this.sub];
    return MAIN[this.screen] || entryView;
  },

  render(){
    const view = this.currentView();
    const isAuth = view === authView;
    root.innerHTML = view.render(this);

    const chrome = !isAuth && !this.sub;
    nav.hidden = !chrome;
    root.classList.toggle('no-nav', !chrome);
    root.classList.toggle('auth-mode', isAuth);

    for (const b of nav.querySelectorAll('[data-nav]')){
      b.setAttribute('aria-current', b.dataset.nav === this.screen ? 'true' : 'false');
    }
    if (typeof view.mount === 'function') view.mount(this, root);
    root.scrollTop = 0;
  },

  go(screen){
    this.screen = screen;
    this.sub = null;
    if (screen === 'report') this.rep.drill = null;
    this.render();
    this.ensureData();
  },

  /**
   * يجلب الفترة التي تحتاجها الشاشة الحالية إن لم تكن محمّلة.
   * الشاشات التي لا تعلن rangeFor (التسجيل، يوم كامل، توزيع، نص) تعمل
   * بلا اتصال كما هي: كل ما تحتاجه فهرس محلي وعدّادات.
   */
  async ensureData(){
    const view = this.currentView();
    if (typeof view.rangeFor !== 'function') return;
    const need = view.rangeFor(this);
    if (!need) return;

    const w = state.window;
    const covered = w && w.f <= +need.from && w.t >= +need.to && w.untyped === !!need.untyped;
    if (covered) return;

    if (!isOnline()){
      this.dataErr = 'offline';
      this.loading = false;
      return this.render();
    }

    this.loading = true;
    this.dataErr = null;
    this.render();
    try {
      await loadRange(need.from, need.to, { untyped: need.untyped });
      this.dataErr = null;
    } catch (err){
      this.dataErr = (err instanceof ApiError && err.isOffline) ? 'offline'
                   : (err.message || 'تعذّر الجلب');
    } finally {
      this.loading = false;
      this.render();
    }
  },

  openSub(name, opts){
    this.sub = name;
    if (name === 'day')      this.dayDraft   = dayView.freshDraft();
    if (name === 'split')    this.splitDraft = splitView.freshDraft();
    if (name === 'text')     this.textDraft  = textView.freshDraft();
    if (name === 'classify') this.clsDraft   = classifyView.freshDraft(opts);
    if (name === 'purge')    this.purgeDraft = purgeView.freshDraft();
    if (name === 'account')  this.accountDraft = accountView.freshDraft();
    if (name === 'devices')  this.devicesDraft = devicesView.freshDraft();
    this.render();
    this.ensureData();
  },

  closeSub(){ this.sub = null; this.render(); },

  openEdit(id){
    const d = editView.draftFrom(id);
    if (!d) return;
    this.editDraft = d;
    this.sub = 'edit';
    this.render();
  },

  /* ------------------------- الدخول والخروج ------------------------- */

  /** يُستدعى بعد نجاح المصادقة: يحمّل المحلي، يرسم، ثم يزامن. */
  async enterApp(){
    loadFor(this.session.user);
    state.user = this.session.user;
    if (this.session.household) state.household = this.session.household;

    this.draft = entryView.freshDraft();
    this.screen = 'entry';
    this.sub = null;
    this.render();          // نرسم من الذاكرة المحلية فوراً — لا شاشة انتظار

    startAutoSync();
    try { await sync({ force: true }); this.render(); }
    catch (err){ if (!(err instanceof ApiError && err.isAuth)) console.warn(err); }
  },

  async logout(){
    // بلا اتصال لا تُلغى الجلسة على الخادم، والكوكي httpOnly فلا يمسحه JS.
    // المسح المحلي وحده يعطي شعوراً كاذباً بالخروج: أول فتح مع اتصال يسأل /me
    // فيجد الجلسة حيّة فيعيد المستخدم إلى حسابه وهو يظنّ أنه خرج.
    if (!isOnline()){
      return this.toast('تسجيل الخروج يحتاج اتصالاً — جلستك على الخادم لا تُلغى بلا إنترنت');
    }
    try { await api.logout(); }
    catch (err){
      // انقطاع أثناء الطلب نفسه — أمّا 401 فيعني أن الجلسة ساقطة أصلاً،
      // ومسح المحلي حينها هو الصواب
      if (err instanceof ApiError && err.isOffline){
        return this.toast('انقطع الاتصال — لم يكتمل تسجيل الخروج');
      }
    }
    clearLocal();
    this.session = null;
    this.sub = null;
    this.authDraft = authView.freshDraft();
    this.render();
    this.toast('سجّلت خروجك');
  },

  /** إعادة رسم بعد تغيير محلي. */
  async refresh(){
    try { await sync({ force: true }); } catch { /* الطابور يصبر */ }
    this.render();
  },

  /** إعادة سحب كاملة — بعد الانضمام لمحفظة أخرى أو استيراد كبير. */
  async hardRefresh(){
    state.lastSync = null;
    state.dests = []; state.types = []; state.entries = [];
    store.clearWindow();
    try {
      const me = await api.me();
      state.user = me.user; state.household = me.household;
      this.session = { user: me.user, household: me.household };
    } catch { /* نكمل بما لدينا */ }
    try { await sync({ force: true }); } catch { /* ignore */ }
    this.render();
  },

  /** مزامنة غير حاجزة بعد كتابة محلية. */
  queueSync(){
    sync().catch(err => {
      if (err instanceof ApiError && err.isAuth) this.sessionExpired();
    });
  },

  sessionExpired(){
    if (!this.session) return;
    this.session = null;
    this.authDraft = authView.freshDraft();
    this.authDraft.error = 'انتهت الجلسة — سجّل دخولك من جديد';
    this.render();
  },

  /* --------------------------- التراجع --------------------------- */

  undoEntries(ids){
    removeEntries(ids);
    this.render();
    this.toast('تم التراجع');
    this.queueSync();
  },

  restore(list){
    restoreEntries(list);
    this.render();
    this.toast('أُرجعت الحركة');
    this.queueSync();
  },

  /* --------------------------- الحاسبة --------------------------- */

  /**
   * يفتح نافذة الحاسبة على قيمة ابتدائية، وينادي onAccept بالناتج.
   * الشاشات لا تعرف شيئاً عن النافذة — تمرّر قيمة وتستقبل قيمة.
   */
  /**
   * يفتح الدليل على قسم الشاشة الحالية.
   * المفتاح يُشتق من حالة التنقّل نفسها، فلا خريطة وسيطة تُنسى عند إضافة شاشة.
   */
  openHelp(key){
    this.helpKey = key || help.keyFor(this);
    this.renderModal();
  },

  closeHelp(){
    this.helpKey = null;
    this.renderModal();
  },

  openCalc(initialValue, onAccept){
    this.helpKey = null;                 // حاوية واحدة: الدليل يفسح للحاسبة
    this.calcModal = { state: calc.fromNumber(initialValue), onAccept };
    this.renderModal();
  },

  renderModal(){
    if (this.helpKey){
      const t = help.topic(this.helpKey);
      if (t){
        modal.innerHTML = helpSheet(t, this.helpKey, help.ORDER);
        modal.hidden = false;
        const body = modal.querySelector('.hbody');
        if (body) body.scrollTop = 0;
        return;
      }
    }
    if (!this.calcModal){
      modal.hidden = true;
      modal.innerHTML = '';
      return;
    }
    // نافذة مفتوحة: نحدّث أرقامها فقط — إعادة البناء تُعيد تشغيل حركة الانزلاق
    const open = modal.querySelector('.sheet');
    if (open) return updateCalcModal(open, this.calcModal.state, baseCurrency());
    modal.innerHTML = calcModalHtml(this.calcModal.state, baseCurrency());
    modal.hidden = false;
  },

  closeCalc(){
    this.calcModal = null;
    this.renderModal();
  },

  /* --------------------------- الإشعار --------------------------- */

  toast(message, actionLabel, action){
    clearTimeout(this._toastTimer);
    toastMsg.textContent = message;
    if (actionLabel && typeof action === 'function'){
      toastAct.textContent = actionLabel;
      this._toastAction = action;
    } else {
      toastAct.textContent = '';
      this._toastAction = null;
    }
    toastEl.classList.add('on');
    toastEl.style.bottom = (this.session && !this.sub)
      ? 'calc(var(--nav-h) + var(--safe-b) + 12px)'
      : 'calc(var(--safe-b) + 12px)';
    this._toastTimer = setTimeout(() => this.hideToast(), 5200);
  },

  hideToast(){
    toastEl.classList.remove('on');
    this._toastAction = null;
  }
};

/* ------------------------------ الأحداث ------------------------------ */

root.addEventListener('click', ev => {
  const btn = ev.target.closest('button');
  if (!btn || btn.disabled) return;

  // زرّ الدليل واحد في كل الشاشات — يُعالَج هنا قبل أن تراه الشاشة
  if (btn.dataset.act === 'help') return app.openHelp();

  const view = app.currentView();
  let handled = false;
  if (typeof view.onClick === 'function'){
    handled = view.onClick(app, btn);
    // بعض المعالجات غير متزامنة — نلتقط أخطاءها حتى لا تسقط بصمت
    if (handled && typeof handled.catch === 'function'){
      handled.catch(err => {
        console.error(err);
        app.toast(err instanceof ApiError ? err.message : 'حدث خطأ');
      });
      return;
    }
  }
  if (handled === false && btn.dataset.act === 'back'){
    if (app.sub){ app.editDraft = null; app.closeSub(); }
  }
});

nav.addEventListener('click', ev => {
  const b = ev.target.closest('[data-nav]');
  if (b) app.go(b.dataset.nav);
});

/* نافذة الحاسبة — تُعالَج هنا لا في الشاشات، فتستفيد منها كلها بلا تكرار */
modal.addEventListener('click', ev => {
  // الضغط على الخلفية خارج البطاقة يغلق النافذة
  if (ev.target === modal) return app.helpKey ? app.closeHelp() : app.closeCalc();

  const btn = ev.target.closest('button');
  if (!btn || btn.disabled) return;

  // ---- ورقة الدليل ----
  if (btn.dataset.helpgo) return app.openHelp(btn.dataset.helpgo);
  if (btn.dataset.helpact === 'close') return app.closeHelp();

  if (!app.calcModal) return;

  if (btn.dataset.calckey){
    app.calcModal.state = calc.press(app.calcModal.state, btn.dataset.calckey);
    return app.renderModal();
  }

  const act = btn.dataset.calcact;
  if (act === 'clear'){
    app.calcModal.state = calc.create();
    return app.renderModal();
  }
  if (act === 'cancel') return app.closeCalc();
  if (act === 'accept'){
    const value = calc.value(app.calcModal.state);
    const done = app.calcModal.onAccept;
    app.closeCalc();
    if (value !== null && value > 0 && done) done(value);
  }
});

toastAct.addEventListener('click', () => {
  const fn = app._toastAction;
  app.hideToast();
  if (fn) fn();
});

// زر الرجوع في الهاتف يغلق الشاشة الفرعية بدل مغادرة التطبيق
window.addEventListener('popstate', () => {
  // زر الرجوع يغلق الحاسبة ثم الدليل ثم الشاشة الفرعية — بترتيب فتحها
  if (app.calcModal){
    app.closeCalc();
    history.pushState({ fw: 1 }, '');
    return;
  }
  if (app.helpKey){
    app.closeHelp();
    history.pushState({ fw: 1 }, '');
    return;
  }
  if (app.sub){
    app.editDraft = null;
    app.closeSub();
    history.pushState({ fw: 1 }, '');
  }
});

document.addEventListener('keydown', ev => {
  if (ev.key !== 'Escape') return;
  // الحاسبة تُغلق الدليل عند فتحها، فلا يجتمعان — والترتيب هنا احتياط لا أكثر
  if (app.calcModal) return app.closeCalc();
  if (app.helpKey) app.closeHelp();
});

document.addEventListener('visibilitychange', () => {
  if (document.visibilityState === 'hidden') saveNow();
});
window.addEventListener('pagehide', saveNow);

/* ------------------------- شريط الحالة العلوي ------------------------- */

function updateBanner(status, queued){
  if (!banner) return;
  let text = '', cls = '';
  if (!isOnline() || status === SyncStatus.OFFLINE){
    text = queued ? `بلا اتصال · ${queued} حركة بانتظار الإرسال` : 'بلا اتصال — التطبيق يعمل عادةً';
    cls = 'offline';
  } else if (status === SyncStatus.ERROR){
    text = 'تعذّرت المزامنة — سنعيد المحاولة'; cls = 'error';
  } else if (status === SyncStatus.SYNCING && queued){
    text = `جارٍ إرسال ${queued} حركة…`; cls = 'sync';
  }
  banner.textContent = text;
  banner.className = 'banner' + (text ? ' on ' + cls : '');
}

onSyncChange((status, queued) => {
  updateBanner(status, queued);
  if (status === SyncStatus.AUTH) app.sessionExpired();
});
onConnectivity(() => updateBanner(SyncStatus.IDLE, pendingCount()));

/* ------------------------------ الإقلاع ------------------------------ */

async function boot(){
  applyTheme();
  history.pushState({ fw: 1 }, '');

  // نرسم من آخر مستخدم معروف قبل انتظار الشبكة: فتح التطبيق بلا إنترنت
  // يجب أن يُظهر البيانات فوراً لا شاشة تحميل تنتهي بخطأ.
  const known = lastUser();
  if (known){
    loadFor(known);
    app.session = { user: known, household: state.household };
    app.draft = entryView.freshDraft();
    app.render();
  } else {
    app.render();
  }

  // ثم نتحقّق من الجلسة فعلياً مع الخادم
  try {
    const me = await api.me();
    app.session = { user: me.user, household: me.household };
    if (!known || known.id !== me.user.id) loadFor(me.user);
    state.user = me.user;
    state.household = me.household;
    app.draft = entryView.freshDraft();
    app.render();
    startAutoSync();
    await sync({ force: true });
    app.render();
  } catch (err){
    if (err instanceof ApiError && err.isOffline){
      // بلا اتصال: نُبقي الجلسة المحلية عاملة — هذا جوهر العمل دون إنترنت
      updateBanner(SyncStatus.OFFLINE, pendingCount());
    } else {
      clearLocal();
      app.session = null;
      app.authDraft = authView.freshDraft();
      app.render();
    }
  }

  app.ready = true;

  if ('serviceWorker' in navigator && window.isSecureContext){
    if (import.meta.env.DEV){
      // في التطوير: أسماء الملفات بلا بصمات، وعامل الخدمة يقدّم الأصول من المخزَّن أولاً،
      // فيبقى المتصفّح على نسخة قديمة من الشيفرة (ومن رمز Vite فتفشل WebSocket).
      // نزيل أي تسجيل سابق ومخزّنه بدل أن نسجّل.
      navigator.serviceWorker.getRegistrations()
        .then(rs => rs.forEach(r => r.unregister()));
      caches.keys()
        .then(ks => ks.filter(k => k.startsWith('fw-')).forEach(k => caches.delete(k)));
    } else {
      navigator.serviceWorker.register('sw.js').catch(err => {
        console.info('لم يُسجَّل عامل الخدمة (يحتاج HTTPS أو localhost):', err && err.message);
      });
    }
  }
}

boot();

// للتشخيص من وحدة التحكم عند الحاجة.
// state تُعاد إسنادها عند تسجيل الدخول (loadFor)، فلو نسخنا المرجع مرة واحدة
// لبقي يشير إلى الكائن القديم الفارغ ولأعطى قراءات خاطئة عند التشخيص.
window.familyWallet = {
  app,
  sync,
  isOnline,                       // للتشخيص: نسخة الوحدة التي يستعملها التطبيق فعلاً
  get state(){ return store.state; }
};
